import { inject, Injectable, NgZone, signal } from '@angular/core';
import { firstValueFrom, Subject } from 'rxjs';

import { VoiceHttpService } from '../voice-http.service';

export type VoiceWhatsappStatus = 'idle' | 'connecting' | 'active';

/** Same STUN the backend's WhatsApp peer uses (twinny-webrtc); no TURN for now. */
const ICE_SERVERS: RTCIceServer[] = [{ urls: 'stun:stun.l.google.com:19302' }];
/** Meta doesn't do trickle ICE — wait for gathering to finish, then send what we have. */
const ICE_GATHERING_TIMEOUT_MS = 8_000;
/** 'disconnected' can be a blip; only 'failed' / 'closed' are final on the spot. */
const DISCONNECT_GRACE_MS = 4_000;

/** Thrown when the agent's browser refuses the microphone. */
export class VoiceMicDeniedError extends Error {
    constructor() {
        super('Microphone permission denied');
        this.name = 'VoiceMicDeniedError';
    }
}

/**
 * WhatsApp Cloud calls: the agent's browser negotiates a direct WebRTC leg with the
 * backend — no Telnyx SDK, no SIP address. `pickup` builds the peer connection, sends
 * the SDP offer on POST /voice/calls/{id}/pickup and applies the answer; audio then
 * flows both ways. `hangup` tells the backend and tears the leg down. One call at a
 * time, mirroring {@link VoiceWebrtcClientService}.
 */
@Injectable({ providedIn: 'root' })
export class VoiceWhatsappClientService {
    private readonly voiceS = inject(VoiceHttpService);
    private readonly zone = inject(NgZone);

    readonly status = signal<VoiceWhatsappStatus>('idle');
    readonly muted = signal(false);
    /** Fires when the live call ends — locally or because the caller / Meta hung up. */
    readonly callEnded$ = new Subject<void>();

    private pc?: RTCPeerConnection;
    private localStream?: MediaStream;
    private remoteAudio?: HTMLAudioElement;
    private callControlId?: string;
    private disconnectTimer?: ReturnType<typeof setTimeout>;

    /**
     * Negotiate the call. Resolves once the backend accepted the offer (audio is on its
     * way; `status` flips to 'active' when ICE connects). Rejects with the HTTP error
     * (status 403 / 409 / 502 / …) or {@link VoiceMicDeniedError}; nothing is left open on failure.
     * `pickupToken` is the originate 200's `pickup_token` for outbound calls (KAN-238).
     */
    async pickup(callControlId: string, pickupToken?: string): Promise<void> {
        if (this.status() !== 'idle') throw new Error('WhatsApp call already in progress');
        this.status.set('connecting');
        this.callControlId = callControlId;

        let localStream: MediaStream;
        try {
            localStream = await navigator.mediaDevices.getUserMedia({ audio: true });
        } catch {
            this.status.set('idle');
            this.callControlId = undefined;
            throw new VoiceMicDeniedError();
        }

        const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS });
        this.pc = pc;
        this.localStream = localStream;
        this.muted.set(false);
        localStream.getTracks().forEach(track => pc.addTrack(track, localStream));

        const audio = this.ensureRemoteAudio();
        pc.ontrack = event => {
            audio.srcObject = event.streams[0] ?? new MediaStream([event.track]);
            void audio.play().catch(() => undefined);
        };
        pc.oniceconnectionstatechange = () => this.onIceStateChange(pc);

        try {
            const offer = await pc.createOffer();
            await pc.setLocalDescription(offer);
            await this.waitForIceGathering(pc);

            const { sdp_answer } = await firstValueFrom(
                this.voiceS.pickupWhatsappCall$(callControlId, {
                    sdp_offer: pc.localDescription?.sdp ?? offer.sdp ?? '',
                    // KAN-238: outbound calls need the originator's token; harmless on inbound.
                    ...(pickupToken ? { pickup_token: pickupToken } : {}),
                })
            );
            // A hang-up can race the negotiation — don't apply an answer to a dead leg.
            if (this.pc !== pc) return;
            await pc.setRemoteDescription({ type: 'answer', sdp: sdp_answer });
        } catch (err) {
            this.release();
            throw err;
        }
    }

    /** The agent hangs up: tell the backend, then drop the leg. */
    async hangup(): Promise<void> {
        const id = this.callControlId;
        const wasLive = this.status() !== 'idle';
        this.release();
        if (id) {
            try {
                await firstValueFrom(this.voiceS.hangupWhatsappCall$(id));
            } catch {
                /* the leg is closed on our side either way */
            }
        }
        if (wasLive) this.callEnded$.next();
    }

    /** Drop the leg without telling the backend (negotiation never completed). */
    abort() {
        const wasLive = this.status() !== 'idle';
        this.release();
        if (wasLive) this.callEnded$.next();
    }

    mute() {
        this.setMicEnabled(false);
        this.muted.set(true);
    }

    unmute() {
        this.setMicEnabled(true);
        this.muted.set(false);
    }

    toggleMute() {
        this.muted() ? this.unmute() : this.mute();
    }

    /* ── internals ──────────────────────────────────────────────── */

    private onIceStateChange(pc: RTCPeerConnection) {
        if (this.pc !== pc) return;
        // WebRTC callbacks run outside Angular's zone — bring signal writes back in.
        this.zone.run(() => {
            switch (pc.iceConnectionState) {
                case 'connected':
                case 'completed':
                    clearTimeout(this.disconnectTimer);
                    this.status.set('active');
                    break;
                case 'disconnected':
                    clearTimeout(this.disconnectTimer);
                    this.disconnectTimer = setTimeout(() => {
                        if (this.pc === pc && pc.iceConnectionState === 'disconnected') {
                            this.endedRemotely();
                        }
                    }, DISCONNECT_GRACE_MS);
                    break;
                case 'failed':
                case 'closed':
                    this.endedRemotely();
                    break;
            }
        });
    }

    /** The backend closed the peer (caller hung up / Meta ended the call). */
    private endedRemotely() {
        if (this.status() === 'idle') return;
        this.release();
        this.callEnded$.next();
    }

    private release() {
        clearTimeout(this.disconnectTimer);
        const pc = this.pc;
        this.pc = undefined;
        this.callControlId = undefined;
        if (pc) {
            pc.ontrack = null;
            pc.oniceconnectionstatechange = null;
            try {
                pc.close();
            } catch {
                /* already closed */
            }
        }
        this.localStream?.getTracks().forEach(t => t.stop());
        this.localStream = undefined;
        if (this.remoteAudio) this.remoteAudio.srcObject = null;
        this.muted.set(false);
        this.status.set('idle');
    }

    private setMicEnabled(enabled: boolean) {
        this.localStream?.getAudioTracks().forEach(t => (t.enabled = enabled));
    }

    private waitForIceGathering(pc: RTCPeerConnection): Promise<void> {
        if (pc.iceGatheringState === 'complete') return Promise.resolve();
        return new Promise(resolve => {
            const done = () => {
                clearTimeout(timer);
                pc.removeEventListener('icegatheringstatechange', onChange);
                resolve();
            };
            const onChange = () => {
                if (pc.iceGatheringState === 'complete') done();
            };
            const timer = setTimeout(done, ICE_GATHERING_TIMEOUT_MS);
            pc.addEventListener('icegatheringstatechange', onChange);
        });
    }

    private ensureRemoteAudio(): HTMLAudioElement {
        if (this.remoteAudio) return this.remoteAudio;
        const el = document.createElement('audio');
        el.id = 'voice-whatsapp-remote-audio';
        el.autoplay = true;
        el.style.display = 'none';
        document.body.appendChild(el);
        this.remoteAudio = el;
        return el;
    }
}
